import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import {
  LayoutDashboard, TrendingUp, TrendingDown, ShoppingCart, Package,
  Warehouse, BookOpen, RefreshCw, List, History, Trash2, Users, Search, Receipt
} from 'lucide-react';

const ALL_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', keywords: 'dashboard home overview stats summary', icon: LayoutDashboard, adminOnly: false },
  { path: '/income', label: 'Income', keywords: 'income earnings revenue money received', icon: TrendingUp, adminOnly: false },
  { path: '/expense', label: 'Expense', keywords: 'expense spending cost payment outgoing', icon: TrendingDown, adminOnly: false },
  { path: '/sales', label: 'Sales List', keywords: 'sales billing invoice sell', icon: ShoppingCart, adminOnly: false },
  { path: '/sales?tab=customers', label: 'Customer List', keywords: 'customer client buyer party cust', icon: ShoppingCart, adminOnly: false },
  { path: '/sales?tab=outstanding', label: 'Sales Outstanding', keywords: 'sales outstanding dues pending receivable credit', icon: ShoppingCart, adminOnly: false },
  { path: '/purchase', label: 'Purchase Order List', keywords: 'purchase order po buy order list', icon: Package, adminOnly: false },
  { path: '/purchase?tab=entries', label: 'Purchase Entry List', keywords: 'purchase entry pe receipt goods received grn', icon: Package, adminOnly: false },
  { path: '/purchase?tab=suppliers', label: 'Supplier List', keywords: 'supplier vendor firm contact group list', icon: Package, adminOnly: false },
  { path: '/purchase?tab=outstanding', label: 'Purchase Outstanding', keywords: 'purchase outstanding pending remaining due payable', icon: Package, adminOnly: false },
  { path: '/inventory', label: 'Inventory Stock', keywords: 'inventory stock raw material finished goods warehouse', icon: Warehouse, adminOnly: false },
  { path: '/inventory?tab=products', label: 'Product List', keywords: 'product item catalog category brand list', icon: Warehouse, adminOnly: false },
  { path: '/ledger?tab=customer-ledger', label: 'Customer Ledger', keywords: 'customer ledger balance account party', icon: BookOpen, adminOnly: false },
  { path: '/ledger?tab=user-ledger', label: 'User Ledger', keywords: 'user ledger balance account staff employee', icon: BookOpen, adminOnly: false },
  { path: '/ledger?tab=entry-ledger', label: 'Entry Ledger', keywords: 'entry ledger journal book record', icon: BookOpen, adminOnly: false },
  { path: '/recurring', label: 'Recurring - All', keywords: 'recurring repeat monthly subscription auto all', icon: RefreshCw, adminOnly: false },
  { path: '/recurring?tab=due', label: 'Recurring - Due', keywords: 'recurring due today pending post', icon: RefreshCw, adminOnly: false },
  { path: '/recurring?tab=pending', label: 'Recurring - Pending Approval', keywords: 'recurring pending approval admin approve', icon: RefreshCw, adminOnly: true },
  { path: '/recurring?tab=stopped', label: 'Recurring - Stopped', keywords: 'recurring stopped inactive paused', icon: RefreshCw, adminOnly: false },
  { path: '/all-entries', label: 'All Entries', keywords: 'all entries unified view combined list', icon: List, adminOnly: false },
  { path: '/gst', label: 'GST Settings - HSN Master', keywords: 'gst hsn tax cgst sgst igst settings', icon: Receipt, adminOnly: false },
  { path: '/gst?tab=company', label: 'GST Settings - Company', keywords: 'gst company gstin state settings', icon: Receipt, adminOnly: false },
  { path: '/audit-trail', label: 'Audit Trail', keywords: 'audit trail log history changes track', icon: History, adminOnly: true },
  { path: '/deleted-records', label: 'Deleted Records', keywords: 'deleted records trash restore soft permanent', icon: Trash2, adminOnly: true },
  { path: '/users', label: 'Users', keywords: 'users accounts admin people manage', icon: Users, adminOnly: true },
];

export const QuickOpen = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const items = ALL_ITEMS.filter(item => !item.adminOnly || isAdmin);

  const filtered = items.filter(item => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return item.label.toLowerCase().includes(q) || item.keywords.toLowerCase().includes(q);
  });

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (listRef.current && filtered.length > 0) {
      const el = listRef.current.children[selectedIndex];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, filtered.length]);

  const goTo = useCallback((item) => {
    onClose();
    const [path, qs] = item.path.split('?');
    const params = qs ? new URLSearchParams(qs) : null;
    const tab = params?.get('tab');

    const currentPath = window.location.pathname;
    if (currentPath === path) {
      if (tab) {
        window.dispatchEvent(new CustomEvent('quickopen-tab', { detail: { tab } }));
      } else {
        const defaultTabs = { '/purchase': 'orders', '/inventory': 'stock', '/sales': 'sales' };
        if (defaultTabs[path]) {
          window.dispatchEvent(new CustomEvent('quickopen-tab', { detail: { tab: defaultTabs[path] } }));
        }
      }
    } else {
      navigate(path);
      setTimeout(() => {
        if (tab) {
          window.dispatchEvent(new CustomEvent('quickopen-tab', { detail: { tab } }));
        }
      }, 150);
    }
  }, [navigate, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[selectedIndex]) goTo(filtered[selectedIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [filtered, selectedIndex, goTo, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="quick-open-overlay">
      {/* Backdrop - matches Shadcn Dialog overlay */}
      <div
        className="fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        onClick={onClose}
      />

      {/* Dialog - matches Shadcn DialogContent styling */}
      <div
        className="fixed left-[50%] top-[20%] z-50 w-full max-w-lg translate-x-[-50%] border bg-background shadow-lg sm:rounded-lg overflow-hidden"
        data-testid="quick-open-dialog"
      >
        {/* Search input - uses Shadcn Input styling */}
        <div className="flex items-center gap-3 px-4 border-b">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Where do you want to go?"
            className="border-0 shadow-none rounded-none focus-visible:ring-0 h-11 px-0"
            data-testid="quick-open-input"
          />
          <kbd className="hidden sm:inline-flex h-5 items-center rounded-md border bg-muted px-1.5 text-[10px] font-mono text-muted-foreground">ESC</kbd>
        </div>

        {/* Results list */}
        <div ref={listRef} className="max-h-[340px] overflow-y-auto p-1" data-testid="quick-open-results">
          {filtered.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="quick-open-empty">
              No results for "{query}"
            </div>
          ) : (
            filtered.map((item, idx) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => goTo(item)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm rounded-md transition-colors ${
                    idx === selectedIndex
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                  data-testid={`quick-open-item-${item.label.toLowerCase().replace(/[\s\/]+/g, '-')}`}
                >
                  <Icon className={`h-4 w-4 shrink-0 ${idx === selectedIndex ? 'text-primary-foreground' : 'text-muted-foreground'}`} />
                  <span className="font-medium">{item.label}</span>
                  {item.adminOnly && (
                    <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-md border bg-muted text-muted-foreground font-medium">Admin</span>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Footer - muted bar matching app hint bar style */}
        <div className="flex items-center gap-4 px-4 py-2 border-t bg-muted/50 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1"><kbd className="inline-flex h-[18px] items-center rounded border bg-background px-1 font-mono text-[10px]">&uarr;&darr;</kbd> Navigate</span>
          <span className="flex items-center gap-1"><kbd className="inline-flex h-[18px] items-center rounded border bg-background px-1 font-mono text-[10px]">Enter</kbd> Open</span>
          <span className="flex items-center gap-1"><kbd className="inline-flex h-[18px] items-center rounded border bg-background px-1 font-mono text-[10px]">Esc</kbd> Close</span>
        </div>
      </div>
    </div>
  );
};
